import { Injectable, OnDestroy } from '@angular/core';
import {
  ExpireSoonDescriptionStr,
  ExpireSoonStr,
  SessionExpiredMessage,
} from '@piqet-angular/shared';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import * as jwt_decode from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import * as UserDetailsActions from '../../+state/user-details/user-details.actions';
import * as sharedAction from '../../+state/shared/shared.actions';
import { CookieHelperService } from 'libs/shared/src/lib/service/cookie-helper.service';
import { AuthFacade } from '@piqet-angular/data';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class SimultaneousService implements OnDestroy {
  isLoginMethod: boolean = false;
  warningDisplayed: boolean = false;
  sessionExtensionListener: any;
  tokenRefreshListener: any;
  sessionWarningTimerId: any = null;
  autoLogoutTimerId: any = null;

  // Use a unique identifier for each tab
  private tabId: string = Math.random().toString(36).substring(2, 15);

  constructor(
    private swalService: SwalService,
    private cookieService: CookieService,
    private store: Store,
    private toast: ToastrService,
    private cookieHelper: CookieHelperService,
    private authFacade: AuthFacade
  ) {
    this.sessionExtensionListener = (event: any) => {
      if (event.key === 'session_extension_requested' && event.newValue) {
        const data = JSON.parse(event.newValue);
        if (data.tabId !== this.tabId) {
          this.clearWarningState();
          Swal.close();
        }
      }
    };

    // Listen for token refresh notifications from other tabs
    this.tokenRefreshListener = (event: any) => {
      if (event.key === 'token_refreshed' && event.newValue) {
        const data = JSON.parse(event.newValue);
        if (data.tabId !== this.tabId) {
          this.resetSessionTimers(data.expiryTime);
        }
      }
    };

    window.addEventListener('storage', this.sessionExtensionListener);
    window.addEventListener('storage', this.tokenRefreshListener);
  }

  resetLoginMethod() {
    this.isLoginMethod = false;
  }

  clearWarningState() {
    this.warningDisplayed = false;

    // Clear any pending timers
    if (this.sessionWarningTimerId) {
      clearTimeout(this.sessionWarningTimerId);
      this.sessionWarningTimerId = null;
    }

    if (this.autoLogoutTimerId) {
      clearTimeout(this.autoLogoutTimerId);
      this.autoLogoutTimerId = null;
    }
  }

  // New method to reset timers based on token expiry
  resetSessionTimers(expiryTime: number) {
    this.clearWarningState();

    const expiry = new Date(expiryTime);
    const warningTime = expiry.getTime() - 5 * 60 * 1000;
    const currentTime = Date.now();

    if (warningTime > currentTime) {
      const timeUntilWarning = warningTime - currentTime;
      this.setupWarningTimer(timeUntilWarning);
    }
  }

  // Set up the warning timer
  setupWarningTimer(timeUntilWarning: number) {
    this.sessionWarningTimerId = setTimeout(() => {
      // this.showSessionWarning();
    }, timeUntilWarning);
  }

  // Show session warning with cross-tab coordination
  showSessionWarning() {
    const token = this.cookieService.get('token');
    if (!token) return;

    this.warningDisplayed = true;

    // Set auto-logout timer
    this.autoLogoutTimerId = setTimeout(() => {
      this.authFacade.logout();
    }, 297000); // 4 minutes 57 seconds

    this.swalService.confirm(ExpireSoonStr, ExpireSoonDescriptionStr).then(confirmed => {
      if (confirmed) {
        // Notify other tabs that we're extending the session
        localStorage.setItem(
          'session_extension_requested',
          JSON.stringify({
            tabId: this.tabId,
            timestamp: Date.now(),
          })
        );

        const token = this.cookieService.get('token');
        if (!token) {
          this.clearWarningState();
          this.toast.error(SessionExpiredMessage);
          return;
        }

        // Dispatch the token regeneration action
        this.store.dispatch(
          UserDetailsActions.regenerateAccessToken({ sessionString: 'sessionString' })
        );
      } else {
        this.clearWarningState();
      }
    });
  }

  logoutWarningPopup(data: any, flag?: boolean) {
    if (this.isLoginMethod || this.warningDisplayed) {
      return;
    }

    this.isLoginMethod = flag || true;
    const tokenExpiry: any = jwt_decode.jwtDecode(data?.data?.token || data);
    const expiry = new Date(tokenExpiry.exp * 1000);

    const warningTime = expiry.getTime() - 5 * 60 * 1000;
    // console.table([
    //   {
    //     'Event': 'Token Expiry',
    //     'Time': expiry.toLocaleString(),
    //     'In Minutes': ((expiry.getTime() - Date.now()) / 60000).toFixed(2)
    //   },
    //   {
    //     'Event': 'Warning Time',
    //     'Minutes': Math.floor((expiry.getTime() - Date.now() - 5 * 60 * 1000) / 60000),
    //     'Seconds': Math.floor(((expiry.getTime() - Date.now() - 5 * 60 * 1000) % 60000) / 1000)
    //   }
    // ]);

    const currentTime = Date.now();
    if (warningTime > currentTime) {
      const timeUntilWarning = warningTime - currentTime;

      // Setup the warning timer
      this.setupWarningTimer(timeUntilWarning);
    }
  }

  // Call this after token regeneration success
  notifyTokenRefreshed(token: string) {
    const tokenExpiry: any = jwt_decode.jwtDecode(token);
    const expiryTime = tokenExpiry.exp * 1000;

    // Reset our own timers
    this.resetSessionTimers(expiryTime);
    // Notify other tabs
    localStorage.setItem(
      'token_refreshed',
      JSON.stringify({
        tabId: this.tabId,
        timestamp: Date.now(),
        expiryTime: expiryTime,
      })
    );
  }

  ngOnDestroy() {
    this.clearWarningState();

    if (this.sessionExtensionListener) {
      window.removeEventListener('storage', this.sessionExtensionListener);
    }

    if (this.tokenRefreshListener) {
      window.removeEventListener('storage', this.tokenRefreshListener);
    }
  }
}
